import type {
  CreateJobResponse,
  CreateJobResponseViewModel,
  JobError,
  JobErrorViewModel,
  JobListItem,
  JobListResponse,
  JobListViewModel,
  JobOwner,
  JobOwnerViewModel,
  JobStatusResponse,
  JobViewModel,
  MappingJobNotification,
  MappingJobWebSocketMessage,
  MatchInfo,
  MatchInfoViewModel,
  ResultExportResponse,
  ResultExportViewModel,
  RowContext,
  RowContextViewModel,
} from "@models/job";

export const buildJobErrorViewModel = (
  error: JobError | null,
): JobErrorViewModel | null => {
  if (!error) return null;
  return {
    errorCode: error.error_code,
    message: error.message,
    description: error.description,
  };
};

export const buildMatchInfoViewModel = (
  match: MatchInfo,
): MatchInfoViewModel => ({
  rank: match.rank,
  key: match.key,
  distance: match.distance,
  similarity: match.similarity ?? null,
  score: match.score ?? null,
  categoryScore: match.category_score ?? null,
  metadata: match.metadata,
});

export const buildRowContextViewModel = (
  context: RowContext | null,
): RowContextViewModel | null => {
  if (!context) return null;
  return { ...context };
};

export const buildJobViewModelFromStatus = (
  response: JobStatusResponse,
): JobViewModel => ({
  jobId: response.job_id,
  pluginId: response.plugin_id,
  status: response.status,
  inputKind: response.input_kind,
  filename: response.filename,
  rowsTotal: response.rows_total,
  rowsDone: response.rows_done,
  acceptedCount: response.accepted_count ?? 0,
  error: buildJobErrorViewModel(response.error),
  createdAt: response.created_at,
  updatedAt: response.updated_at,
});

export const buildJobOwnerViewModel = (
  owner: JobOwner | null,
): JobOwnerViewModel | null => {
  if (!owner) return null;
  return {
    userId: owner.user_id,
    email: owner.email,
    firstName: owner.first_name,
    lastName: owner.last_name,
  };
};

export const buildJobViewModelFromListItem = (
  item: JobListItem,
): JobViewModel => ({
  jobId: item.job_id,
  pluginId: item.plugin_id,
  service: item.service,
  status: item.status,
  inputKind: item.input_kind,
  filename: item.filename,
  rowsTotal: item.rows_total,
  rowsDone: item.rows_done,
  acceptedCount: 0,
  error: buildJobErrorViewModel(item.error),
  owner: buildJobOwnerViewModel(item.created_by ?? null),
  createdAt: item.created_at,
  updatedAt: item.updated_at,
});

export const buildCreateJobResponseViewModel = (
  response: CreateJobResponse,
): CreateJobResponseViewModel => ({
  jobId: response.job_id,
  status: response.status,
  uploadUrl: response.upload_url,
  uploadContentType: response.upload_content_type,
  uploadExpiresAt: response.upload_expires_at,
  maxBytes: response.max_bytes,
});

export const buildResultExportViewModel = (
  response: ResultExportResponse,
): ResultExportViewModel => ({
  downloadUrl: response.download_url,
  filename: response.filename,
  rowCount: response.row_count,
});

export const buildJobListViewModel = (
  response: JobListResponse,
): JobListViewModel => ({
  jobs: response.jobs.map(buildJobViewModelFromListItem),
  cursor: response.cursor,
});

export const buildMappingJobNotification = (
  message: MappingJobWebSocketMessage,
): MappingJobNotification => {
  if (message.type === "mapping.job.progress") {
    return {
      type: message.type,
      jobId: message.job_id,
      status: message.status,
      rowsDone: message.rows_done,
      rowsTotal: message.rows_total,
    };
  }
  return {
    type: message.type,
    jobId: message.job_id,
    pluginId: message.plugin_id,
    status: message.status,
    resultUrlPath: message.result_url_path,
  };
};
